import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { supabase } from '../supabase/client';
import { Search, X } from 'lucide-react';
import ProfileRow from './ProfileRow';

const TAG = '[UserSearch]';
const SEARCH_DEBOUNCE_MS = 500;

/**
 * UserSearch - Reusable component for searching users with custom row buttons
 *
 * @param {function} onSelected - Callback when a user is selected (user) => void
 * @param {Array} rowButtons - Array of button configs to display on right side of rows
 *                            Each button: { icon: Component, label: string, onClick: (user) => void, className?: string }
 * @param {string} placeholder - Placeholder text for search input
 * @param {string} containerClassName - Optional className for the container
 */
function UserSearch({ onSelected, rowButtons = [], placeholder = 'Search @tag or name...', containerClassName = '' }) {
  const meId = useSelector((s) => s.user.authUserId);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Search by share code (@tag)
  const searchByShareCode = async (searchQuery) => {
    const code = searchQuery.slice(1).toLowerCase();
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, images, share_code')
      .eq('share_code', code);

    if (error) throw error;
    return data;
  };

  // Search by name in chats
  const searchByName = async (searchQuery) => {
    const { data: res, error } = await supabase.rpc('search_by_name_in_chats_j', {
      p_user_id: meId,
      p_query: searchQuery
    });

    if (error) throw error;
    return (res ?? []).map(r => ({
      id: r.profile_id,
      name: r.name,
      images: r.images,
      is_chat_connection: r.is_chat_connection
    }));
  };

  // Main search function - checks for @ and calls appropriate search
  const performSearch = async (searchQuery) => {
    setSearching(true);
    try {
      let data;
      if (searchQuery.startsWith('@')) {
        data = await searchByShareCode(searchQuery);
      } else {
        data = await searchByName(searchQuery);
      }
      // Deduplicate results by ID
      const uniqueData = data ? data.filter((user, index, self) =>
        index === self.findIndex((u) => u.id === user.id)
      ) : [];
      setResults(uniqueData);
    } catch (err) {
      /* Error handled */
    } finally {
      setSearching(false);
    }
  };

  // Search logic with debounce to prevent API calls on every keystroke
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      performSearch(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(delayDebounce);
  }, [query, meId]);

  // Handle user selection - calls internal logic then callback
  const handleUserSelect = (user) => {
    // Internal logic could go here (e.g., tracking, analytics, etc.)
    // Then call the provided callback
    if (onSelected) {
      onSelected(user);
    }
  };

  return (
    <div className={`space-y-3 ${containerClassName}`}>
      <div className="flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-2 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm focus:outline-none"
        />
        {query && (
          <button onClick={() => setQuery('')}>
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Search Results */}
      {query.length >= 2 && (
        <div className="rounded-2xl border border-border bg-card/50 overflow-hidden">
          {searching ? (
            <div className="flex justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No users found
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {results.map((user) => (
                <li key={user.id} className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <ProfileRow 
                      profile={user} 
                      size="md" 
                      subtitle={user.is_chat_connection ? 'In your chats' : undefined}
                      boldSubtitle={true}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {rowButtons.map((buttonConfig, index) => {
                      const ButtonIcon = buttonConfig.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            buttonConfig.onClick(user);
                            handleUserSelect(user);
                          }}
                          className={`flex h-8 items-center gap-1.5 rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 ${buttonConfig.className || ''}`}
                        >
                          {ButtonIcon && <ButtonIcon className="h-3.5 w-3.5" />}
                          {buttonConfig.label}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default UserSearch;
